/**
 * E145: Web Push enrollment helper.
 *
 * Wires the browser's PushManager to the cleanroom backend (E144) — fetches
 * VAPID public key, requests Notification permission, subscribes via the SW
 * registration's pushManager, and posts the subscription to /api/push/subscribe.
 *
 * Returns one of: "granted" | "denied" | "unsupported" | "already_enabled" | "failed".
 * Caller surfaces the result in UI.
 */

type EnableResult =
  | "granted"
  | "denied"
  | "unsupported"
  | "already_enabled"
  | "failed";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export async function enableWebPush(
  sessionToken: string | undefined,
): Promise<EnableResult> {
  if (
    typeof navigator === "undefined" ||
    !("serviceWorker" in navigator) ||
    !("PushManager" in window) ||
    !("Notification" in window)
  ) {
    return "unsupported";
  }

  let permission = Notification.permission;
  if (permission === "default") {
    try {
      permission = await Notification.requestPermission();
    } catch {
      return "failed";
    }
  }
  if (permission !== "granted") return "denied";

  try {
    const reg = await navigator.serviceWorker.ready;

    // If already subscribed, treat as success.
    const existing = await reg.pushManager.getSubscription();
    if (existing) return "already_enabled";

    const vapidResp = await fetch("/api/push/vapid", {
      headers: { Accept: "application/json" },
      credentials: "omit",
    });
    if (!vapidResp.ok) return "failed";
    const { public_key: vapidPublicKey } = (await vapidResp.json()) as {
      public_key?: string;
    };
    if (!vapidPublicKey) return "failed";

    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });

    const headers: Record<string, string> = {
      "content-type": "application/json",
    };
    if (sessionToken) headers["x-session-token"] = sessionToken;

    const subResp = await fetch("/api/push/subscribe", {
      method: "POST",
      headers,
      body: JSON.stringify(subscription.toJSON()),
    });
    if (!subResp.ok) return "failed";
    return "granted";
  } catch {
    return "failed";
  }
}

export async function disableWebPush(
  sessionToken: string | undefined,
): Promise<EnableResult> {
  if (
    typeof navigator === "undefined" ||
    !("serviceWorker" in navigator) ||
    !("PushManager" in window)
  ) {
    return "unsupported";
  }

  try {
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    if (!existing) return "denied";

    const headers: Record<string, string> = {
      "content-type": "application/json",
    };
    if (sessionToken) headers["x-session-token"] = sessionToken;

    await fetch("/api/push/unsubscribe", {
      method: "POST",
      headers,
      body: JSON.stringify({ endpoint: existing.endpoint }),
    });
    await existing.unsubscribe();
    return "granted";
  } catch {
    return "failed";
  }
}

export async function isWebPushEnabled(): Promise<boolean> {
  if (
    typeof navigator === "undefined" ||
    !("serviceWorker" in navigator) ||
    !("PushManager" in window)
  ) {
    return false;
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    return !!existing;
  } catch {
    return false;
  }
}
